import type { WhatsappBroadcastRecipientInput } from '@/lib/api/messaging/whatsapp-broadcasts-api'

/** Divide uma linha de CSV respeitando aspas ("Silva; Maria" é um campo só). */
function splitLine(line: string, delimiter: string): string[] {
    const out: string[] = []
    let current = ''
    let quoted = false
    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') {
            if (quoted && line[i + 1] === '"') {
                current += '"'
                i++
            } else {
                quoted = !quoted
            }
        } else if (ch === delimiter && !quoted) {
            out.push(current.trim())
            current = ''
        } else {
            current += ch
        }
    }
    out.push(current.trim())
    return out
}

/** Aceita AAAA-MM-DD (com ou sem hora) e DD/MM/AAAA. */
function parseDate(value: string | undefined): string | null {
    const v = (value ?? '').trim()
    if (!v) return null
    const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(v)
    const date = br ? new Date(`${br[3]}-${br[2]}-${br[1]}T12:00:00`) : new Date(v)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const HEADERS: Record<keyof WhatsappBroadcastRecipientInput, RegExp> = {
    phone: /^(telefone|phone|celular|whatsapp|numero|número)$/i,
    name: /^(nome|name|contato)$/i,
    lastActivityAt: /^(ultima_atividade|última_atividade|ultima atividade|última atividade|data|last_activity_at|lastactivityat)$/i,
    externalRef: /^(referencia|referência|ref|external_ref|externalref|id)$/i,
}

/**
 * Lê o CSV do público: colunas telefone, nome, última atividade e referência.
 * Com cabeçalho, a ordem não importa; sem cabeçalho, vale essa ordem.
 */
export function parseRecipientsCsv(text: string): { rows: WhatsappBroadcastRecipientInput[]; ignored: number } {
    const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim())
    if (lines.length === 0) return { rows: [], ignored: 0 }

    const first = lines[0]
    const delimiter = [';', '\t', ','].reduce((best, d) => (first.split(d).length > first.split(best).length ? d : best), ';')
    const head = splitLine(first, delimiter)
    const hasHeader = head.some(h => HEADERS.phone.test(h))
    const index = (key: keyof WhatsappBroadcastRecipientInput, fallback: number) =>
        hasHeader ? head.findIndex(h => HEADERS[key].test(h)) : fallback

    const cols = {
        phone: index('phone', 0),
        name: index('name', 1),
        lastActivityAt: index('lastActivityAt', 2),
        externalRef: index('externalRef', 3),
    }

    const rows: WhatsappBroadcastRecipientInput[] = []
    let ignored = 0
    for (const line of hasHeader ? lines.slice(1) : lines) {
        const cells = splitLine(line, delimiter)
        const phone = (cells[cols.phone] ?? '').replace(/\D/g, '')
        if (phone.length < 10) {
            ignored++
            continue
        }
        rows.push({
            phone,
            name: cols.name >= 0 ? cells[cols.name] || null : null,
            lastActivityAt: cols.lastActivityAt >= 0 ? parseDate(cells[cols.lastActivityAt]) : null,
            externalRef: cols.externalRef >= 0 ? cells[cols.externalRef] || null : null,
        })
    }
    return { rows, ignored }
}
